import React, { useState, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AddCircleOutline } from "@mui/icons-material";
import { Search } from "lucide-react";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import { DEFAULT_LIMIT, DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../utils/constants";
import { useGetApi } from "../hooks/useGetApi";
import { getCompanyApi } from "../services/company.service";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import { EditIcon } from "../comps/CustomIcons";
import AddCompanyModal from "../components/item_master/modal/company-modal";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";

export default function MasterCompany() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [search, setSearch] = useState("");
  const [searchBy, setSearchBy] = useState("name");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: DEFAULT_LIMIT });
  const [sortModel, setSortModel] = useState([{ field: "name", sort: "asc" }]);
  const [addCompanyModal, setAddCompanyModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const searchByOptions = [
    { value: "code", label: "Company Code" },
    { value: "name", label: "Company Name" },
    { value: "shortName", label: "Person" },
  ];
  const searchByLabel = searchByOptions.find((o) => o.value === searchBy)?.label || "Company Name";

  const handleModalOpen = () => setAddCompanyModal(true);
  const handleModalClose = () => { setAddCompanyModal(false); setSelectedRow(null); };
  const handleEdit = (rowData) => { setSelectedRow(rowData._original || rowData); handleModalOpen(); };

  const { dataList: itemList, dataCount: itemCount, isLoading, isError, refetch } = useGetApi({
    apiFunction: getCompanyApi,
    params: { limit: paginationModel.pageSize, page: paginationModel.page + 1, search, searchBy, sort: sortModel[0]?.field || "name", order: sortModel[0]?.sort || "asc" },
    dependencies: [search, searchBy, paginationModel.pageSize, paginationModel.page, sortModel],
    debounceDelay: 500,
    dataKey: "companies",
    countKey: "total",
  });

  const rawRows = (itemList || []).map((row) => ({
    id: row._id,
    _id: row._id,
    code: row?.code || "-",
    name: row?.name || "-",
    shortName: row?.shortName || "-",
    status: row?.active ? "Active" : "Inactive",
    _original: row,
  }));

  const sortField = sortModel[0]?.field || "name";
  const sortOrder = sortModel[0]?.sort || "asc";
  const sortedRows = [...rawRows].sort((a, b) => { const cmp = String(a[sortField] ?? "").localeCompare(String(b[sortField] ?? ""), undefined, { numeric: true }); return sortOrder === "asc" ? cmp : -cmp; });
  const pageSize = paginationModel.pageSize;
  const page = paginationModel.page;
  const rows = sortedRows.length > pageSize ? sortedRows.slice(page * pageSize, page * pageSize + pageSize) : sortedRows;

  const getColumns = useCallback(() => [
    { field: "code", headerName: "Company Code", flex: 1, minWidth: 120, headerAlign: "left", align: "left", sortable: true },
    { field: "name", headerName: "Company Name", flex: 1, minWidth: 140, headerAlign: "left", align: "left", sortable: true },
    { field: "shortName", headerName: "Person", flex: 1, minWidth: 100, headerAlign: "left", align: "left", sortable: true },
    { field: "status", headerName: "Status", flex: 0.6, minWidth: 90, headerAlign: "center", align: "center", sortable: true, renderCell: (params) => <Chip label={params.value || "Active"} color={params.value === "Inactive" ? "error" : "success"} size="small" variant="outlined" /> },
    { field: "actions", headerName: "Actions", flex: 0, width: 70, minWidth: 60, headerAlign: "center", align: "center", sortable: false, renderCell: (params) => <EditIcon sx={{ color: "transparent", fontSize: "18px", cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); handleEdit(params.row); }} /> },
  ], []);

  const dataGridSx = {
    border: "none", "& .MuiDataGrid-root": { border: "none", width: "100%" }, "& .MuiDataGrid-cell": { borderBottom: "1px solid #f0f0f0", padding: isMobile ? "4px" : "8px", fontSize: isMobile ? "12px" : "14px" },
    "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f5f5f5", borderBottom: "2px solid #e0e0e0" }, "& .MuiDataGrid-columnHeader": { backgroundColor: "#f5f5f5", fontWeight: "bold" },
    "& .MuiDataGrid-row": { "&:hover": { backgroundColor: "#f5f5f5" }, "&:nth-of-type(odd)": { backgroundColor: "#fafafa" }, "&:nth-of-type(even)": { backgroundColor: "white" } },
    "& .MuiDataGrid-footerContainer": { borderTop: "2px solid #e0e0e0", backgroundColor: "#f9f9f9" },
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box><Sidebar /></Box>
        <Box sx={{ width: "100%", pr: "90px", paddingBottom: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ color: "#5D6A82", textTransform: "none", fontSize: "16px", fontWeight: "bold", padding: 0, transition: "none !important", mt: 2 }}>Back</Button>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 2 }}>
            <Typography sx={{ color: "rgba(93, 106, 130, 1)", fontSize: 20 }}>Company Master</Typography>
          </Box>
          <Box sx={{ my: "20px", display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: 2 }}>
            <TextField placeholder={`Search by ${searchByLabel}`} size="small" sx={{ width: "300px" }} slotProps={{ input: { startAdornment: (
              <InputAdornment position="start">
                <select className="uid-select" value={searchBy} onChange={(e) => setSearchBy(e.target.value)} style={{ border: "none", outline: "none", background: "transparent", cursor: "pointer", color: "#333", fontWeight: "500" }}>
                  {searchByOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Search />
              </InputAdornment>
            ) } }} value={search || ""} onChange={(e) => setSearch(e.target.value || "")} />
            <Button variant="contained" sx={{ textTransform: "none", display: "flex", alignItems: "center", gap: 0.5 }} onClick={handleModalOpen}>
              <AddCircleOutline /><Typography sx={{ color: "white" }}>New Company</Typography>
            </Button>
          </Box>
          {isLoading ? <Loader /> : isError ? <ErrorMessage /> : (
            <Box sx={{ height: { xs: "calc(100vh - 300px)", sm: "calc(100vh - 320px)", md: "calc(100vh - 350px)" }, width: "100%", minHeight: "300px", overflow: "hidden" }}>
              <DataGrid rows={rows} columns={getColumns()} paginationModel={paginationModel} onPaginationModelChange={(m) => setPaginationModel(m)} pageSizeOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS} paginationMode="server" rowCount={itemCount} sortingMode="server" sortModel={sortModel} onSortModelChange={(m) => setSortModel(m)} disableRowSelectionOnClick loading={isLoading} sx={dataGridSx} components={{ NoRowsOverlay: () => <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}><Typography variant="body2" color="text.secondary">No companies found</Typography></Box> }} />
            </Box>
          )}
          <AddCompanyModal open={addCompanyModal} onClose={handleModalClose} refetch={refetch} selectedRow={selectedRow} />
        </Box>
      </Box>
    </Container>
  );
}
